import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@xanectech/ui/components';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useCompanySearch } from '@xanectech/data';
import { Company } from '@xanectech/types';

interface CompanySearchInputProps {
  value: string | null;
  onChange: (companyId: string | null, company?: Company | null) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  id?: string;
}

const formatCompanyLabel = (company: Company | null) => {
  if (!company) return '';

  if (company.name && company.company_code) {
    return `${company.name} · ${company.company_code}`;
  }
  if (company.name) return company.name;
  return company.company_code || company.id;
};

export const CompanySearchInput = ({
  value,
  onChange,
  placeholder = 'Search companies by name or code...',
  disabled = false,
  required = false,
  name = 'companyId',
  id,
}: CompanySearchInputProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 300);

    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setDebouncedSearch('');
    }
  }, [open]);

  const {
    data: companies,
    isLoading,
    isError,
  } = useCompanySearch({
    query: debouncedSearch,
    limit: 15,
    enabled: open,
  });

  useEffect(() => {
    if (!value) {
      setSelectedCompany(null);
      return;
    }

    if (companies && Array.isArray(companies)) {
      const match = (companies as Company[]).find(
        (company: Company) => company.id === value,
      );
      if (match) {
        setSelectedCompany(match);
      }
    }
  }, [companies, value]);

  const inputId = useMemo(() => id || name, [id, name]);

  const selectedLabel = useMemo(() => {
    if (selectedCompany) return formatCompanyLabel(selectedCompany);
    if (value) return value;
    return 'Select a company';
  }, [selectedCompany, value]);

  const handleSelect = (company: Company) => {
    setSelectedCompany(company);
    onChange(String(company.id), company);
    setOpen(false);
  };

  return (
    <div className='w-full'>
      <input
        type='hidden'
        name={name}
        value={value ?? ''}
        required={required}
        aria-hidden='true'
      />

      <Popover
        open={open}
        onOpenChange={(nextOpen) => {
          if (disabled) return;
          setOpen(nextOpen as boolean);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type='button'
            variant='outline'
            role='combobox'
            aria-expanded={open}
            aria-label='Select company'
            id={inputId}
            className='w-full justify-between'
            disabled={disabled}
          >
            <span className='truncate'>{selectedLabel}</span>
            <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
          </Button>
        </PopoverTrigger>
        <PopoverContent className='p-0' align='start'>
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={placeholder}
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              <CommandEmpty>
                {isError
                  ? 'Unable to load companies.'
                  : isLoading
                    ? 'Searching companies...'
                    : 'No matching companies found.'}
              </CommandEmpty>
              <CommandGroup>
                {((companies as Company[]) || []).map((company: Company) => {
                  const companyLabel = formatCompanyLabel(company);

                  return (
                    <CommandItem
                      key={company.id}
                      value={companyLabel}
                      onSelect={() => handleSelect(company)}
                      className='flex items-center justify-between'
                    >
                      <div className='flex flex-col truncate text-left'>
                        <span className='text-sm font-medium leading-tight'>
                          {company.name}
                        </span>
                        {company.company_code ? (
                          <span className='text-xs text-muted-foreground'>
                            {company.company_code}
                          </span>
                        ) : null}
                      </div>
                      {value === company.id ? (
                        <Check className='ml-2 h-4 w-4 text-primary' />
                      ) : null}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
